/**
 * StudentProfileHeader component
 * Composite component that displays profile icon and student name
 * Used in session chat page header to show student identity
 */

import { cn } from '@/lib/utils';
import { ProfileIconUploader } from '@/components/ProfileIconUploader';
import { StudentNameDisplay } from '@/components/StudentNameDisplay';

export interface StudentProfileHeaderProps {
  /** Student display name from UserProfile */
  displayName: string;
  /** S3 URL of custom profile image, or null/undefined for default */
  profileImageUrl?: string | null;
  /** Optional override classes */
  className?: string;
  /** BUG-20260209-105903: Callback invoked after successful profile image upload - 2026-02-09 */
  onUploadSuccess?: (imageUrl: string) => void;
}

/**
 * StudentProfileHeader - Displays student profile icon and name as a horizontal unit
 * Icon is always visible, name is hidden on screens below 640px (sm breakpoint)
 */
export function StudentProfileHeader({
  displayName,
  profileImageUrl,
  className,
  onUploadSuccess,
}: StudentProfileHeaderProps) {
  return (
    <div
      className={cn('inline-flex items-center gap-1.5', className)}
      role="group"
      aria-label={`Student profile: ${displayName}`}
    >
      <ProfileIconUploader
        profileImageUrl={profileImageUrl}
        size="sm"
        onUploadSuccess={onUploadSuccess}
      />
      <StudentNameDisplay name={displayName} className="hidden sm:inline" />
    </div>
  );
}
